"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useReducer,
  type ReactNode,
} from "react";
import { createApiClient, type ApiClientConfig, type AuthTokens } from "./client";

type ApiClient = ReturnType<typeof createApiClient>;
const ApiClientContext = createContext<ApiClient | null>(null);

export const CART_TOKEN_STORAGE_KEY = "ntr_cart_token";

/** Persist the backend-issued cart token so subsequent requests carry it (guest carts). */
export function persistCartToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CART_TOKEN_STORAGE_KEY, token);
}

interface AuthHolder {
  getToken: () => string | undefined;
  getRefreshToken: () => string | undefined;
  /** Set the access token only (refresh token left unchanged). Kept for compatibility. */
  setToken: (token: string | undefined) => void;
  /** Set access + refresh together (login, signup-complete, refresh rotation). */
  setTokens: (accessToken?: string, refreshToken?: string) => void;
  /** Wipe all in-memory auth (logout / session expiry). */
  clear: () => void;
}

const AuthHolderContext = createContext<AuthHolder | null>(null);

export interface ApiClientProviderProps {
  children: ReactNode;
  baseUrl: string;
  tenantCode?: string;
  defaultLocale?: string;
  cartTokenStorageKey?: string;
  /**
   * Called once when a session that had auth can no longer be refreshed. The app
   * should show a single "session expired" message and redirect to login. Auth
   * state is already cleared by the provider before this runs.
   */
  onSessionExpired?: () => void;
  /**
   * Enable the HttpOnly refresh-cookie flow (BLK-API-014): the 401→refresh path
   * works without an in-memory refresh token, and on mount the provider attempts
   * one silent refresh to restore the session after a full page reload.
   */
  cookieRefresh?: boolean;
  /**
   * Optional cheap gate for the mount-time restore (e.g. "the presence cookie
   * exists") so guests don't pay a doomed refresh call. Only consulted when
   * `cookieRefresh` is on.
   */
  shouldRestoreSession?: () => boolean;
}

export function ApiClientProvider({
  children,
  baseUrl,
  tenantCode = "nutrayan",
  defaultLocale = "en-IN",
  cartTokenStorageKey = "ntr_cart_token",
  onSessionExpired,
  cookieRefresh = false,
  shouldRestoreSession,
}: ApiClientProviderProps) {
  // Tokens live in a ref so the client's getters always read the latest value
  // synchronously — a retried request after refresh must never see a stale token.
  // A forced re-render notifies React consumers (e.g. `useCurrentUser` enablement).
  const tokensRef = useRef<{ access: string | undefined; refresh: string | undefined }>({
    access: undefined,
    refresh: undefined,
  });
  const [, notify] = useReducer((c: number) => c + 1, 0);
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;
  const shouldRestoreRef = useRef(shouldRestoreSession);
  shouldRestoreRef.current = shouldRestoreSession;

  const auth = useMemo<AuthHolder>(
    () => ({
      getToken: () => tokensRef.current.access,
      getRefreshToken: () => tokensRef.current.refresh,
      setToken: (token) => {
        tokensRef.current = { ...tokensRef.current, access: token };
        notify();
      },
      setTokens: (accessToken, refreshToken) => {
        tokensRef.current = { access: accessToken, refresh: refreshToken };
        notify();
      },
      clear: () => {
        tokensRef.current = { access: undefined, refresh: undefined };
        notify();
      },
    }),
    [],
  );

  // Cookie-based session restore (BLK-API-014): after a full reload the in-memory
  // tokens are gone but the HttpOnly refresh cookie survives. The FIRST request's
  // getAuthToken kicks off one silent refresh (raw fetch — not client.request, so
  // it can't await itself) and every request awaits it, so data queries wait for
  // the restored token instead of racing ahead unauthenticated. Single-flight via
  // ref: StrictMode double effects / concurrent queries share one rotation (two
  // parallel rotations of the same token 500 server-side).
  const restoreRef = useRef<{ attempted: boolean; promise: Promise<void> | null }>({
    attempted: false,
    promise: null,
  });
  const restoreFnRef = useRef<(() => Promise<void>) | null>(null);

  // `auth` is stable, so the client is created once — no stale closures on retry.
  const client = useMemo<ApiClient>(() => {
    const restoreSession = (): Promise<void> => {
      const state = restoreRef.current;
      if (state.attempted) return state.promise ?? Promise.resolve();
      state.attempted = true;
      state.promise = (async () => {
        try {
          const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Tenant-Code": tenantCode,
            },
            body: "{}",
          });
          if (!res.ok) return;
          const payload = (await res.json().catch(() => null)) as {
            data?: AuthTokens;
          } | null;
          const tokens = payload?.data;
          if (tokens?.accessToken) auth.setTokens(tokens.accessToken, tokens.refreshToken);
        } catch {
          // Silent: guest, expired cookie, or offline — pages handle signed-out state.
        }
      })();
      return state.promise;
    };
    restoreFnRef.current = restoreSession;

    const config: ApiClientConfig = {
      baseUrl,
      tenantCode,
      defaultLocale,
      getAuthToken: async () => {
        if (
          cookieRefresh &&
          !auth.getToken() &&
          !restoreRef.current.attempted &&
          (typeof window === "undefined" || !shouldRestoreRef.current || shouldRestoreRef.current())
        ) {
          void restoreSession();
        }
        if (restoreRef.current.promise) await restoreRef.current.promise;
        return auth.getToken();
      },
      getRefreshToken: () => auth.getRefreshToken(),
      cookieRefresh,
      getCartToken: () => {
        if (typeof window === "undefined") return undefined;
        return window.localStorage.getItem(cartTokenStorageKey) ?? undefined;
      },
      onAuthRefreshed: (tokens: AuthTokens) => {
        auth.setTokens(tokens.accessToken, tokens.refreshToken ?? auth.getRefreshToken());
      },
      onAuthCleared: () => {
        auth.clear();
        onSessionExpiredRef.current?.();
      },
    };
    return createApiClient(config);
  }, [baseUrl, tenantCode, defaultLocale, auth, cartTokenStorageKey, cookieRefresh]);

  // Eager restore kick on mount: auth-gated pages render a signed-out state and
  // fire NO request when there is no token, so the lazy (first-request) trigger
  // alone would never run for them. Shares the same single-flight promise.
  useEffect(() => {
    if (!cookieRefresh || auth.getToken() || restoreRef.current.attempted) return;
    if (shouldRestoreRef.current && !shouldRestoreRef.current()) return;
    void restoreFnRef.current?.();
  }, [cookieRefresh, auth]);

  return (
    <ApiClientContext.Provider value={client}>
      <AuthHolderContext.Provider value={auth}>{children}</AuthHolderContext.Provider>
    </ApiClientContext.Provider>
  );
}

export function useApiClient() {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error("useApiClient must be used inside <ApiClientProvider>");
  return ctx;
}

export function useAuthToken() {
  const ctx = useContext(AuthHolderContext);
  if (!ctx) throw new Error("useAuthToken must be used inside <ApiClientProvider>");
  return ctx;
}
